/* eslint - disable */
import React, { FC, useState } from 'react';
import { Button, Col, Divider, Input, Modal, Row, Select, Switch, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DocumentInput, ErrorModal, Form } from '~/components';
import MobileFormItem from '~/components/MobileFormItem';
import WizardForm from '~/components/WizardForm';
import theme from '~/constants/theme';
import { useGetApplication, useGetPayloadWithProvided, useSubmitStep } from '~/controllers/wizard';
import { Draft } from '~/types/Draft';
import { Stage } from '~/types/Application';
import { useCommonErrors } from '~/controllers/validation';
import ContentHeader from '~/components/ContentHeader';
import {
  CloseOutlined,
  ExclamationCircleFilled,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useIsMobile } from '~/hooks/useScreenSizes';
import DynamicSelect from '~/components/DynamicSelect';
import Actions from '~/components/Actions';
import { useHistory, useLocation } from 'react-router';
import DateInput from '~/components/inputs/DateInput';
import { CloseIcon, InfoCircleIcon, PersonOutlinedIcon } from '~/img';
import paths from '~/pages/paths';
import { useGetDraft } from '~/controllers/draft';
import { useDirection } from '~/hooks/i18nHooks';
import { getTrackingId } from '~/utils/handleGetUtm';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

const Managers: FC = () => {
  const [, handleError] = useCommonErrors();
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showError, setShowError] = useState<{ visible: boolean; error: any }>({
    visible: false,
    error: null,
  });
  const [form] = Form.useForm<Draft<'managers'>>();
  const { t } = useTranslation('ksaApplication');
  const applicationQuery: any = useGetApplication();
  const location = useLocation();
  const history = useHistory();
  const getDraft = useGetDraft();
  const submitStep = useSubmitStep();
  const isMobile = useIsMobile();
  const [showModal, setShowModal] = useState<any>(false);
  const { TextArea } = Input;
  const proofOptions = [
    {
      label: t(
        'This person is listed on the Company License as part of the company ownership structure'
      ),
      value: 'DOC_NOT_REQUIRED',
    },
    {
      label: t(
        'Upload a copy of the Power of Attorney showing this person as an authorised signatory'
      ),
      value: 'DOC_REQUIRED',
    },
  ];
  const dir = useDirection();
  const validatePersonalPhoneNumber = (value: any) => {
    if (!value) {
      return Promise.resolve();
    }

    if (value.length < 12) {
      return Promise.reject('Please enter a valid Personal Identification Number');
    }

    return Promise.resolve();
  };

  const validateEmail = (value: any) => {
    if (!value) {
      return Promise.resolve();
    }
    const emailRegEx =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (emailRegEx.test(value)) {
      return Promise.resolve();
    }

    return Promise.reject('Please enter a valid email address');
  };

  dayjs.extend(customParseFormat);
  function validateIssueDate(value: string) {
    if (!value) return Promise.resolve();

    let issueDate;

    if (dayjs(value, 'YYYY-MM-DD', true).isValid()) {
      issueDate = dayjs(value, 'YYYY-MM-DD', true);
    } else if (dayjs(value, 'YYYY-MM', true).isValid()) {
      issueDate = dayjs(value, 'YYYY-MM', true);
    } else {
      return Promise.reject(new Error(t('Please enter a valid issue date')));
    }

    if (issueDate.isAfter(dayjs(), 'day')) {
      return Promise.reject(
        new Error(t('Issue date can not be in the future'))
      );
    }

    return Promise.resolve();
  }

  function validateExpiryDate(value: string) {
    // Optional field
    if (!value) {
      return Promise.resolve();
    }

    let expiryDate;

    // Support YYYY-MM-DD
    if (dayjs(value, 'YYYY-MM-DD', true).isValid()) {
      expiryDate = dayjs(value, 'YYYY-MM-DD', true);
    }
    // Support YYYY-MM
    else if (dayjs(value, 'YYYY-MM', true).isValid()) {
      expiryDate = dayjs(value, 'YYYY-MM', true);
    } else {
      return Promise.reject(
        new Error(t('Please enter a valid expiry date'))
      );
    }

    // Expiry date cannot be in the past
    if (expiryDate.isBefore(dayjs(), 'day')) {
      return Promise.reject(
        new Error(t('Expiry date can not be in the past'))
      );
    }

    return Promise.resolve();
  }

  function beforeSubmit(values: any) {
    const owners = values?.managers?.managers
      ?.concat(values?.shareholders?.individuals)
      ?.filter((item: any) => item?.personalData?.isAuthorizedSignatory);

    if (!!owners.length) {
      (window as any).dataLayer.push({
        event: 'event_formsubmit',
        page_ln: 'en',
        page_name: window.location.href,
        page_source: document.referrer || 'direct',
        page_section: 'Business Owner',
        trackingId: getTrackingId(applicationQuery?.data),
        user_id: localStorage.getItem('sob_temp_uid'),
      });

      return values;
    }
    (window as any).dataLayer.push({
      event: 'event_fielderror',
      page_ln: 'en',
      page_name: window.location.href,
      page_source: document.referrer || 'direct',
      form_fieldname: 'isAuthorizedSignatory',
      form_fielderror: 'At least one shall be selected as authorized signatory',
      trackingId: getTrackingId(applicationQuery?.data),
      user_id: localStorage.getItem('sob_temp_uid'),
    });
    setShowErrorMessage(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const initialValues =
    getDraft?.data || {
      managers: applicationQuery?.data?.payload?.managers
        ? { ...applicationQuery?.data?.payload?.managers }
        : {},
      shareholders: applicationQuery?.data?.payload?.shareholders
        ? { ...applicationQuery?.data?.payload?.shareholders }
        : {},
      companyQuestionnaire: applicationQuery?.data?.payload?.companyQuestionnaire
        ? { ...applicationQuery?.data?.payload?.companyQuestionnaire }
        : {},
    } ||
    {};
  if (!applicationQuery?.data) return null;

  return (
    <Container dir={dir}>
      <>
        <Modal centered open={showErrorMessage} footer={null}>
          <div style={{ textAlign: 'right' }}>
            <CloseIcon
              onClick={() => setShowErrorMessage(false)}
              style={{ fontSize: 12, color: '#5b5b60', marginBottom: 20 }}
            />
          </div>
          <div style={{ padding: 20, direction: dir, textAlign: dir === 'rtl' ? 'right' : 'left' }}>
            <Typography.Paragraph>
              {t('Please select at least one authorized signatory.')}
            </Typography.Paragraph>
          </div>
        </Modal>
        <ContentHeader
          title={t('Business Owner')}
          subtitle={t('Review owner details and disclose roles.')}
          stageNumber={3}
        />
        <WizardForm<any>
          validateTrigger="onSubmit"
          payloadKey="managers"
          form={form}
          initialValues={initialValues}
          parseBeforeSubmit={beforeSubmit}
          stage={Stage.ksaApplication}
          onFinishFailed={({ errorFields }: any) => {
            if (errorFields?.[0]?.name) {
              form.scrollToField(errorFields[0].name, { behavior: 'smooth', block: 'center' });
            }
          }}
          onFinish={async (values: any) => {
            if (!values) return; // beforeSubmit returned undefined (no authorized signatory)
            try {
              await submitStep.mutateAsync(values);
            } catch (error: any) {
              const errorData = error?.response?.data;
              setShowError({ visible: true, error: errorData });
            }
          }}
        >
          <Typography.Paragraph className="subTitle1">{t('Owner Details')}</Typography.Paragraph>
          {applicationQuery?.data?.payload?.provided?.shareholders?.individuals?.map(
            (item: any, index: number) => {
              return (
                // eslint-disable-next-line react/jsx-key

                <PersonCard dir={dir} isMobile={isMobile}>
                  <PersonOutlinedIcon className="user-icon" />
                  <Form.Item
                    hidden
                    initialValue={item?.uid}
                    name={['shareholders', 'individuals', index, 'uid']}
                  />
                  <Form.Item
                    hidden
                    initialValue={item?.ownershipShare}
                    name={['shareholders', 'individuals', index, 'ownershipShare']}
                  />
                  <Form.Item
                    initialValue={item?.personalDocument}
                    name={['shareholders', 'individuals', index, 'personalDocument']}
                    hidden
                  />
                  <Form.Item
                    hidden
                    initialValue={item?.personalData}
                    name={['shareholders', 'individuals', index, 'personalData']}
                  />
                  {!!Object?.keys(item?.passport || {})?.length && (
                    <>
                      <Form.Item
                        initialValue={item?.passport?.documentNumber}
                        name={[
                          'shareholders',
                          'individuals',
                          index,
                          'personalDocument',
                          'idNumber',
                        ]}
                        hidden
                      />
                      <Form.Item
                        initialValue="passport"
                        name={['shareholders', 'individuals', index, 'personalDocument', 'idType']}
                        hidden
                      />
                    </>
                  )}
                  <Section dir={dir}>
                    <Typography.Paragraph className="lbaelPersonCard">
                      {t('Owner')}
                    </Typography.Paragraph>

                    <Typography.Paragraph className="valuePersonCArd">
                      {item.personalData?.fullName}
                    </Typography.Paragraph>
                  </Section>

                  <Section dir={dir}>
                    <Typography.Paragraph className="lbaelPersonCard">
                      {t('Nationality')}
                    </Typography.Paragraph>

                    <Typography.Paragraph className="valuePersonCArd">
                      {item.personalData?.nationality}
                    </Typography.Paragraph>
                  </Section>
                  <Section dir={dir}>
                    <Typography.Paragraph className="lbaelPersonCard">
                      {t('Government Issued ID')}
                    </Typography.Paragraph>

                    <Typography.Paragraph className="valuePersonCArd">
                      {item.personalDocument?.idNumber}
                    </Typography.Paragraph>
                  </Section>
                  {!!Object?.keys(item?.passport || {})?.length && (
                    <>
                      <Div dir={dir}>
                        <div
                          style={{
                            fontFamily: dir === 'rtl' ? 'Tajawal,sans-serif' : 'Inter,sans-serif',
                          }}
                        >
                          <ExclamationCircleFilled style={{ marginTop: 6, color: '#004B7E' }} />
                          <span
                            style={{
                              fontWeight: 600,
                              fontFamily: dir === 'rtl' ? 'Tajawal' : 'Inter',
                            }}
                          >
                            {t('Your Government Issued ID is linked to your passport.')}
                          </span>
                          {t(
                            'Please have your passport ready to provide its issue and expiry dates. '
                          )}
                        </div>
                      </Div>

                      <Form.Item
                        rules={[
                          {
                            required: true,
                            message: t('Please select Authorized Signatory Proof'),
                          },
                          {
                            validator: (_: any, value: any) => validateIssueDate(value),
                          },
                        ]}
                        labelText={t('Passport Issue Date')}
                        name={['shareholders', 'individuals', index, 'passport', 'issueDate']}
                      >
                        <DateInput disableDateRule="date <= today" />
                      </Form.Item>
                      <Form.Item
                        rules={[
                          {
                            validator: (_: any, value: any) => validateExpiryDate(value),
                          },
                        ]}
                        labelText={t('Passport Expiry Date')}
                        name={['shareholders', 'individuals', index, 'passport', 'expiryDate']}
                      >
                        <DateInput disableDateRule='date > today' />
                      </Form.Item>
                    </>
                  )}
                  <Divider type="horizontal" style={{ margin: 0 }} />
                  <Row justify="space-between" align="middle">
                    <Col flex="auto">
                      <Typography.Paragraph
                        className="lbaelPersonCard"
                        style={{
                          marginTop: "15px"
                        }}
                      >
                        {t('This person is an authorized signatory')}
                      </Typography.Paragraph>
                    </Col>

                    <Col>
                      {(() => {
                        const govId = item.personalDocument?.idNumber;
                        const isValidId = /^[12]\d{9}$/.test(govId ?? '');
                        return (
                          <Form.Item
                            noStyle
                            valuePropName="checked"
                            initialValue={false}
                            name={[
                              'shareholders',
                              'individuals',
                              index,
                              'personalData',
                              'isAuthorizedSignatory',
                            ]}
                          >
                            <Switch disabled={!isValidId} />
                          </Form.Item>
                        );
                      })()}
                    </Col>
                  </Row>
                  <Form.ShouldUpdate
                    name={[
                      'shareholders',
                      'individuals',
                      index,
                      'personalData',
                      'isAuthorizedSignatory',
                    ]}
                  >
                    {({ getFieldValue }) => {
                      const isAuthorizedSignatory = getFieldValue([
                        'shareholders',
                        'individuals',
                        index,
                        'personalData',
                        'isAuthorizedSignatory',
                      ]);
                      const needUploadProofs = getFieldValue([
                        'shareholders',
                        'individuals',
                        index,
                        'proof',
                      ]);

                      return (
                        isAuthorizedSignatory && (
                          <AuthSection dir={dir}>
                            <Form.Item labelText={t('Mobile Number')} className="mobileNumberItem">
                              <MobileFormItem
                                name={[
                                  'shareholders',
                                  'individuals',
                                  index,
                                  'personalData',
                                  'phoneNumber',
                                ]}
                                rules={[
                                  { required: true, message: t('Please enter your mobile number') },
                                  {
                                    validator: (_: any, value: any) =>
                                      validatePersonalPhoneNumber(value),
                                    message: t('Please enter a valid mobile number'),
                                  },
                                ]}
                              />
                            </Form.Item>
                            <Form.Item
                              rules={[
                                { required: true, message: t('Please enter your email address.') },
                                {
                                  validator: (_: any, value: any) => validateEmail(value),
                                  message: t('Please enter a valid email address'),
                                },
                              ]}
                              labelText={t('Email')}
                              name={['shareholders', 'individuals', index, 'personalData', 'email']}
                            >
                              <Input placeholder={'signatory@company.com'} />
                            </Form.Item>
                            <Form.Item
                              rules={[
                                {
                                  required: true,
                                  message: t('Please select Authorized Signatory Proof'),
                                },
                              ]}
                              labelText={t('Authorized Signatory Proof')}
                              name={['shareholders', 'individuals', index, 'proof']}
                            >
                              {proofOptions && (
                                <DynamicSelect
                                  placeholder={t(
                                    'This person is listed on the Company License as part of the company ownership structure'
                                  )}
                                  optionsBackUp={proofOptions}
                                />
                              )}
                            </Form.Item>
                            {needUploadProofs === 'DOC_REQUIRED' && (
                              <Form.Item
                                rules={[
                                  {
                                    required: true,
                                    message: t('Please upload authorized signatory proof.'),
                                  },
                                ]}
                                labelText={t('Authorization Letter')}
                                name={['shareholders', 'individuals', index, 'files']}
                              >
                                <DocumentInput
                                  multiple={false}
                                  name="photos"
                                  variant="default"
                                  infoIcon={<InfoCircleIcon />}
                                />
                              </Form.Item>
                            )}
                          </AuthSection>
                        )
                      );
                    }}
                  </Form.ShouldUpdate>

                  {/* <Divider
                    style={{ marginTop: 0, fontWeight: 600, background: '#DADADA' }}
                    type="horizontal"
                  /> */}
                </PersonCard>
              );
            }
          )}

          {applicationQuery?.data?.payload?.provided?.managers?.managers?.map(
            (item: any, index: number) => {
              return (
                // eslint-disable-next-line react/jsx-key
                <PersonCard dir={dir} isMobile={isMobile}>
                  <PersonOutlinedIcon className="user-icon" />
                  <Form.Item
                    hidden
                    initialValue={item?.uid}
                    name={['managers', 'managers', index, 'uid']}
                  />
                  <Form.Item
                    hidden
                    initialValue={item?.personalData}
                    name={['managers', 'managers', index, 'personalData']}
                  />
                  <Form.Item
                    initialValue={item?.personalDocument}
                    name={['managers', 'managers', index, 'personalDocument']}
                    hidden
                  />
                  <Form.Item
                    initialValue={item?.passport}
                    name={['managers', 'managers', index, 'passport']}
                    hidden
                  />
                  {!!Object?.keys(item?.passport || {})?.length && (
                    <>
                      <Form.Item
                        initialValue={item?.passport?.documentNumber}
                        name={['managers', 'managers', index, 'personalDocument', 'idNumber']}
                        hidden
                      />
                      <Form.Item
                        initialValue="passport"
                        name={['managers', 'managers', index, 'personalDocument', 'idType']}
                        hidden
                      />
                    </>
                  )}

                  <Section dir={dir}>
                    <Typography.Paragraph className="lbaelPersonCard">
                      {t('Manager')}
                    </Typography.Paragraph>
                    <Typography.Paragraph className="valuePersonCArd">
                      {item.personalData?.fullName}
                    </Typography.Paragraph>
                  </Section>
                  <Section dir={dir}>
                    <Typography.Paragraph className="lbaelPersonCard">
                      {t('Nationality')}
                    </Typography.Paragraph>
                    <Typography.Paragraph className="valuePersonCArd">
                      {item.personalData?.nationality}
                    </Typography.Paragraph>
                  </Section>
                  <Section dir={dir}>
                    <Typography.Paragraph className="lbaelPersonCard">
                      {t('Government Issued ID')}
                    </Typography.Paragraph>
                    <Typography.Paragraph className="valuePersonCArd">
                      {item.personalDocument?.idNumber || item.passport?.documentNumber}
                    </Typography.Paragraph>
                  </Section>
                  {!!Object?.keys(item?.passport || {})?.length && (
                    <>
                      <Div
                        style={{
                          fontFamily: dir === 'rtl' ? 'Tajawal,sans-serif' : 'Inter,sans-serif',
                        }}
                      >
                        <ExclamationCircleFilled style={{ marginTop: 6, color: '#004B7E' }} />
                        <div>
                          <span style={{ fontWeight: 600 }}>
                            {t('Your Government Issued ID is linked to your passport.')}
                          </span>
                          {t(
                            'Please have your passport ready to provide its issue and expiry dates.'
                          )}
                        </div>
                      </Div>

                      <Form.Item
                        rules={[
                          {
                            validator: (_: any, value: any) => validateIssueDate(value),
                          },
                        ]}
                        labelText={t('Passport Issue Date')}
                        name={['managers', 'managers', index, 'passport', 'issueDate']}
                      >
                        <DateInput disableDateRule="date <= today" />
                      </Form.Item>
                      <Form.Item
                        rules={[
                          {
                            validator: (_: any, value: any) => validateExpiryDate(value),
                          },
                        ]}
                        labelText={t('Passport Expiry Date')}
                        name={['managers', 'managers', index, 'passport', 'expiryDate']}
                      >
                        <DateInput disableDateRule="date > today" />
                      </Form.Item>
                    </>
                  )}
                  <Divider type="horizontal" style={{ margin: 0 }} />
                  <Row justify="space-between" align="middle">
                    <Col flex="auto">
                      <Typography.Paragraph className="lbaelPersonCard">
                        {t('This person is an authorized signatory')}
                      </Typography.Paragraph>
                    </Col>
                    <Col>
                      {(() => {
                        const govId =
                          item.personalDocument?.idNumber ||
                          item.passport?.documentNumber;
                        const isValidId = /^[12]\d{9}$/.test(govId ?? '');
                        return (
                          <Form.Item
                            valuePropName="checked"
                            initialValue={false}
                            name={[
                              'managers',
                              'managers',
                              index,
                              'personalData',
                              'isAuthorizedSignatory',
                            ]}
                          >
                            <Switch disabled={!isValidId} />
                          </Form.Item>
                        );
                      })()}
                    </Col>
                  </Row>
                  <Form.ShouldUpdate
                    name={['managers', 'managers', index, 'personalData', 'isAuthorizedSignatory']}
                  >
                    {({ getFieldValue }) => {
                      const isAuthorizedSignatory = getFieldValue([
                        'managers',
                        'managers',
                        index,
                        'personalData',
                        'isAuthorizedSignatory',
                      ]);
                      const needUploadProofs = getFieldValue([
                        'managers',
                        'managers',
                        index,
                        'proof',
                      ]);
                      return (
                        isAuthorizedSignatory && (
                          <AuthSection dir={dir}>
                            <Form.Item labelText={t('Mobile Number')} className="mobileNumberItem">
                              <MobileFormItem
                                name={['managers', 'managers', index, 'personalData', 'phoneNumber']}
                                rules={[
                                  { required: true, message: t('Please enter your mobile number') },
                                  {
                                    validator: (_: any, value: any) =>
                                      validatePersonalPhoneNumber(value),
                                    message: t('Please enter a valid mobile number'),
                                  },
                                ]}
                              />
                            </Form.Item>
                            <Form.Item
                              rules={[
                                {
                                  required: true,
                                  message: t('Please enter your email'),
                                },
                                {
                                  validator: (_: any, value: any) => validateEmail(value),
                                  message: 'Please enter a valid email address',
                                },
                              ]}
                              labelText={t('Email')}
                              name={['managers', 'managers', index, 'personalData', 'email']}
                            >
                              <Input placeholder={'signatory@company.com'} />
                            </Form.Item>
                            <Form.Item
                              rules={[
                                {
                                  required: true,
                                  message: t('Please select Authorized Signatory Proof'),
                                },
                              ]}
                              labelText={t('Authorized Signatory Proof')}
                              name={['managers', 'managers', index, 'proof']}
                            >
                              {proofOptions && (
                                <DynamicSelect
                                  placeholder={t(
                                    'This person is listed on the Company License as part of the company ownership structure'
                                  )}
                                  optionsBackUp={proofOptions}
                                />
                              )}
                            </Form.Item>
                            {needUploadProofs === 'DOC_REQUIRED' && (
                              <Form.Item
                                name={['managers', 'managers', index, 'files']}
                                rules={[
                                  {
                                    required: true,
                                    message: t('Please upload authorized signatory proof.'),
                                  },
                                ]}
                              >
                                <DocumentInput
                                  labelText={t('Authorization Letter')}
                                  name="photos"
                                  variant="default"
                                  multiple={false}
                                  infoIcon={<InfoCircleIcon />}
                                />
                              </Form.Item>
                            )}
                          </AuthSection>
                        )
                      );
                    }}
                  </Form.ShouldUpdate>
                  {/* <Divider
                    style={{ marginTop: 0, fontWeight: 600, background: '#DADADA' }}
                    type="horizontal"
                  /> */}
                </PersonCard>
              );
            }
          )}
          <QuestionBlock>
            <QuestionHeaderRow>
              <QuestionTitle className="additionText">
                {t(
                  'Are you, or a direct family member, or any individual associated with your business, a Politically Exposed Person (PEP)?'
                )}
              </QuestionTitle>
              <Button
                id="hinIccon"
                type="text"
                icon={<ExclamationCircleOutlined style={{ fontSize: 18, color: '#262626' }} />}
                shape="circle"
                size="small"
                onClick={() => setShowModal({ type: 'sanctionHint', visible: true })}
                style={{ padding: 0, minWidth: 24, height: 24 }}
              />
            </QuestionHeaderRow>

            <ToggleRow >
              <Form.Item
                noStyle
                shouldUpdate
                name={['companyQuestionnaire', 'authorizedRepresentative', 'hasFamilyMembers']}
              >
                {({ getFieldValue }: any) => {
                  const sanction = getFieldValue([
                    'companyQuestionnaire',
                    'authorizedRepresentative',
                    'hasFamilyMembers',
                  ]);
                  return <ToggleLabel>{sanction ? t('Yes') : t('No')}</ToggleLabel>;
                }}
              </Form.Item>
              <Form.Item
                name={['companyQuestionnaire', 'authorizedRepresentative', 'hasFamilyMembers']}
                valuePropName="checked"
                initialValue={false}
                noStyle
              >
                <Switch />
              </Form.Item>
            </ToggleRow>

            <Form.Item
              shouldUpdate
              noStyle
              name={['companyQuestionnaire', 'authorizedRepresentative', 'hasFamilyMembers']}
            >
              {({ getFieldValue }: any) => {
                const sanction = getFieldValue([
                  'companyQuestionnaire',
                  'authorizedRepresentative',
                  'hasFamilyMembers',
                ]);
                return sanction ? (
                  <div style={{ marginTop: 16 }}>
                    <Form.Item
                      rules={[{ required: true, message: t('Please provide the name of the Politically Exposed Person (PEP).') }]}
                      labelText="Name of Politically Exposed Person"
                      name={[
                        'companyQuestionnaire',
                        'authorizedRepresentative',
                        'familyMemberName',
                      ]}
                    >
                      <Input style={{ width: '100%' }} />
                    </Form.Item>
                  </div>
                ) : null;
              }}
            </Form.Item>
          </QuestionBlock>

          <QuestionBlock style={{ marginTop: 32 }}>
            <QuestionHeaderRow>
              <QuestionTitle className="additionText">
                {t(
                  'Do your business activities, owners, or officials have any direct or indirect involvement with, or interests in, any sanctioned countries, territories, or individuals/entities listed on sanctions lists?'
                )}
              </QuestionTitle>
              <Button
                id="hinIccon"
                type="text"
                icon={<ExclamationCircleOutlined style={{ fontSize: 18, color: '#262626' }} />}
                shape="circle"
                size="small"
                onClick={() => setShowModal({ type: 'hasDealingsHint', visible: true })}
                style={{ padding: 0, minWidth: 24, height: 24 }}
              />
            </QuestionHeaderRow>

            <QuestionSubTitle className="additionText">
              {t(
                'This includes any current operations, offices, financial relationships, or planned engagements that may contravene applicable sanctions regulations.'
              )}
            </QuestionSubTitle>

            <ToggleRow>
              <Form.Item
                noStyle
                shouldUpdate
                name={['companyQuestionnaire', 'sanction', 'hasDealings']}
              >
                {({ getFieldValue }: any) => {
                  const sanction = getFieldValue([
                    'companyQuestionnaire',
                    'sanction',
                    'hasDealings',
                  ]);
                  return <ToggleLabel>{sanction ? t('Yes') : t('No')}</ToggleLabel>;
                }}
              </Form.Item>
              <Form.Item
                name={['companyQuestionnaire', 'sanction', 'hasDealings']}
                valuePropName="checked"
                initialValue={false}
                noStyle
              >
                <Switch />
              </Form.Item>
            </ToggleRow>

            <Form.Item shouldUpdate noStyle name={['companyQuestionnaire', 'sanction', 'hasDealings']}>
              {({ getFieldValue }: any) => {
                const sanction = getFieldValue([
                  'companyQuestionnaire',
                  'sanction',
                  'hasDealings',
                ]);
                return sanction ? (
                  <div style={{ marginTop: 16 }}>
                    <Form.Item
                      rules={[{ required: true, message: 'Field is required' }]}
                      labelText="Please provide the countries, individuals, business activities, goods/services involved (including their use, origin, and destination)."
                      name={['companyQuestionnaire', 'sanction', 'briefDescription']}
                    >
                      <TextArea autoSize={{ minRows: 3, maxRows: 5 }} maxLength={150} />
                    </Form.Item>
                    <Typography.Paragraph className="charLimitCaption">
                      {t('Maximum 150 characters.')}
                    </Typography.Paragraph>
                  </div>
                ) : null;
              }}
            </Form.Item>
          </QuestionBlock>
        </WizardForm>
      </>
      <StyledModal
        closable={false}
        centered={!isMobile}
        width={isMobile ? '100vw' : '800px'}
        footer={null}
        visible={showModal?.visible}
        onOk={() => setShowModal({ type: '', visible: false })}
        onCancel={() => setShowModal({ type: '', visible: false })}
        isMobile={isMobile}
      >
        <RoleDisclosureWrapper dir={dir} className="roleDisclosure">
          {showModal?.type === 'sanctionHint' ? (
            <>
              <div className="modalHeaderRow">
                <StyledTitle dir={dir}>{t('Who is a Politically Exposed Person (PEP)?')}</StyledTitle>
                <CloseOutlined
                  className="customCloseIcon"
                  onClick={() => setShowModal({ type: '', visible: false })}
                />
              </div>
              <StyledScrollContainer dir={dir} isMobile={isMobile}>
                <p className="heading">
                  {t(
                    'A PEP is an individual who is or has been entrusted with a prominent public role, including:'
                  )}
                </p>
                <ul className="pep-list">
                  <li>
                    {t('Head of state or government, ministers, deputy or assistant ministers')}
                  </li>
                  <li>{t('Senior government or judicial officials')}</li>
                  <li>{t('Members of parliament or senior politicians')}</li>
                  <li>{t('Senior officials of political parties')}</li>
                  <li>{t('Members of courts of auditors or central bank boards')}</li>
                  <li>{t('Ambassadors or high-ranking diplomats')}</li>
                  <li>{t('Senior military officers')}</li>
                  <li>{t('Senior executive or supervisory bodies of state-owned enterprises')}</li>
                </ul>
                <p className="heading">{t('Also Considered PEPs:')}</p>
                <ul className="pep-list">
                  <li>
                    {t('Direct family members, including:')}
                    <ol type="a" className="alpha-list">
                      <li>{t('Spouse or partner (as recognized by law)')}</li>
                      <li>{t('Children and their spouses')}</li>
                      <li>{t('Parents.')}</li>
                    </ol>
                  </li>
                  <li>
                    {t('Close associates, including:')}
                    <ol type="a" className="alpha-list">
                      <li>
                        {t('Individuals with joint ownership or close business ties with the PEP')}
                      </li>
                      <li>{t('Beneficiaries of entities established for the PEP’s benefit')}</li>
                    </ol>
                  </li>
                </ul>

                <p className="noteText">
                  <strong className="noteTag">{t('Note:')}</strong>{' '}
                  {t('Family members and close associates of a PEP are also considered PEPs')}
                </p>
              </StyledScrollContainer>
            </>
          ) : (
            <>
              <div className="modalHeaderRow">
                <StyledTitle dir={dir}>
                  {t('Sanctioned Countries, Territories and Individuals')}
                </StyledTitle>
                <CloseOutlined
                  className="customCloseIcon"
                  onClick={() => setShowModal({ type: '', visible: false })}
                />
              </div>
              <StyledScrollContainer dir={dir} isMobile={isMobile}>
                <p className="subHeading" style={{ marginTop: 16 }}>
                  <span className="heading">{t('Sanctioned Countries/Territories:')}</span>
                  <br />
                  {t(
                    "Iran, Syria, Cuba, North Korea (Democratic People's Republic of Korea), Crimea, Sevastopol, Donetsk, Luhansk, and any other regions designated by relevant authorities."
                  )}
                </p>
                <p className="subHeading" style={{ marginTop: 16 }}>
                  <span className="heading">{t('Sanctioned Parties:')}</span>
                  <br />
                  {t(
                    'Includes individuals or entities listed by the United Nations Security Council, the European Union, the U.S. Office of Foreign Assets Control (OFAC), the UK HMT, or the UAE Supreme Council for National Security (Local Terrorist List).'
                  )}
                </p>
                <p className="noteText">
                  <strong className="noteTag">{t('Note:')}</strong>{' '}
                  {t('This list may be updated periodically by the relevant authorities.')}
                </p>
              </StyledScrollContainer>
            </>
          )}
        </RoleDisclosureWrapper>
      </StyledModal>
      <div className='ContinueButton'>
        <Actions id="uploadDocument_continueButton" formName={location.pathname} />
      </div>

      <ErrorModal
        visible={showError?.visible}
        error={showError?.error}
        onClose={() => setShowError({ visible: false, error: null })}
      />
      {isMobile && (
        <Button
          type="text"
          style={{ width: '100%', marginTop: 20, fontWeight: 600, color: '#0069B1' }}
          onClick={() => {
            history.push(paths.ksa.contactUs);
          }}
        >
          {t('Contact Us')}
        </Button>
      )}
    </Container>
  );
};

const StyledModal = styled(Modal as any) <{ isMobile?: boolean }>`
  .ant-modal-content {
    border-radius: 20px !important;
    padding: 24px 28px !important;
  }
  .ant-modal-close {
    top: 20px !important;
    right: 20px !important;
  }

  @media (max-width: ${theme.breakpoints.md}px) {
    position: fixed !important;
    bottom: 0 !important;
    top: auto !important;
    left: 0 !important;
    right: 0 !important;
    margin: 0 !important;
    width: 100vw !important;
    max-width: 100vw !important;
    padding-bottom: 0 !important;

    .ant-modal-content {
      border-radius: 20px 20px 0 0 !important;
      padding: 20px 16px !important;
    }
    .ant-modal-body {
    padding:0px 10px!important;
  }
  }
`;

const RoleDisclosureWrapper = styled.div<any>`
  display: flex;
  flex-direction: column;
  max-height: 75vh;
  overflow: hidden;
  direction: ${({ dir }) => dir || 'ltr'};
  text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};

  .modalHeaderRow {
    display: flex;
    flex-direction: ${({ dir }) => (dir === 'rtl' ? '' : 'row')};
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 20px;
    width: 100%;

    .customCloseIcon {
      font-size: 20px;
      color: #1c2027;
      cursor: pointer;
      padding: 4px;
      margin-top: -4px;
      &:hover {
        color: #000;
      }
    }
  }

  .heading {
    color: #1c2027;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 22px;
    margin-bottom: 12px;
    margin-top: 16px;
    &:first-of-type {
      margin-top: 0;
    }
  }

  .subHeading {
    color: #595959;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    margin-bottom: 16px;

    .heading {
      color: #1c2027;
      font-weight: 600;
      display: inline-block;
      margin: 0 0 4px 0;
    }
  }

  .pep-list {
    padding-left: 20px;
    margin-bottom: 16px;

    li {
      color: #595959;
      font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
      font-size: 14px;
      line-height: 22px;
      margin-bottom: 8px;
    }
  }

  .alpha-list {
    padding-left: 20px;
    margin-top: 6px;
    margin-bottom: 8px;
    list-style-type: lower-alpha;

    li {
      color: #595959;
      font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
      font-size: 14px;
      line-height: 22px;
      margin-bottom: 6px;
    }
  }

  .noteText {
    color: #595959;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    line-height: 22px;
    margin-top: 20px;
    margin-bottom: 0;

    .noteTag {
      color: #1c2027 !important;
      font-weight: 600 !important;
      display: inline-block !important;
      margin-right: 4px !important;
      margin-left: ${({ dir }) => (dir === 'rtl' ? '4px' : '0')};
    }
  }
`;

const StyledScrollContainer = styled.div<any>`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  color: #595959;
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  font-size: 14px;
  line-height: 22px;
  direction: ${({ dir }) => dir || 'ltr'};
  text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
  padding-right: 4px;
`;

const StyledTitle = styled.h2<any>`
  margin: 0 0 20px;
  font-size: 20px;
  font-weight: 600;
  color: #1c2027;
  direction: ${({ dir }) => dir || 'ltr'};
  text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  padding-right: 24px;
`;

const AuthSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;

  .mobileNumberItem,
  .mobileNumberItem .ant-form-item {
    margin-bottom: 0 !important;
  }

  /* Stretch the phone input wrapper to full width inside AuthSection only */
  .mobileNumberItem .ant-form-item-control-input-content > div,
  .mobileNumberItem .ant-form-item-control-input-content > div > div {
    max-width: 100% !important;
    width: 100% !important;
  }

  &&&,
  &&& .ant-form-item,
  &&& .ant-form-item-control-input,
  &&& .ant-form-item-control,
  &&& .ant-form-item-control-input-content,
  &&& .ant-input-affix-wrapper,
  &&& input,
  &&& select,
  &&& .ant-select,
  &&& .ant-select-selector,
  &&& .phoneInput-country,
  &&& .PhoneInput,
  &&& .PhoneInputInput {
    max-width: 100% !important;
    width: 100% !important;
  }
`;

const Div = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 12px;

  border-radius: 12px;
  background: #e6f0f7;
  color: #070707;
  font-feature-settings: 'case' on;
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  margin-bottom: 16px;
  //padding-bottom: 0;

  /* shadow/sm */
  // box-shadow: 0 2px 6px 0 rgba(0, 89, 150, 0.03) 0 -1px 8px -10px rgba(0, 89, 150, 0.03);
`;
const PersonCard = styled.div<any>`
  color: #000;
  position: relative;
  background: ${({ isMobile }) => (isMobile ? '#fff' : 'transparent')};
  padding: ${({ isMobile }) => (isMobile ? '16px' : '0')};
  padding-top: 30px;
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0px 2px 6px 0px rgba(0, 89, 150, 0.03), 0px -1px 8px -10px rgba(0, 89, 150, 0.03);

  .user-icon {
    position: absolute;
    right: ${({ isMobile, dir }) => (dir === 'ltr' ? (isMobile ? '20px' : '0') : undefined)};
    left: ${({ isMobile, dir }) => (dir === 'rtl' ? (isMobile ? '20px' : '0') : undefined)};
    background: #F5F9FC;
    padding: 10px;
    border-radius: 100px;
    color: #070707;
    font-size: 16px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const Section = styled.div<any>`
  display: flex;
  flex-direction: column;
  gap: 20;
  //padding: 0;
  // margin-bottom: 16px;  


  .lbaelPersonCard {
    color: #1C2027;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; 
    letter-spacing: 0.14px; 
    margin-bottom:5px !important;
  }

  .valuePersonCArd {
    color: #000;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 450;
    line-height: 24px; /* 171.429% */
    margin-bottom:20px !important;
  }
`;

const Container = styled.div<any>`
  width: 100%;

  &&&,
  &&& .ant-form-item,
  &&& .ant-form-item-control-input,
  &&& .ant-form-item-control,
  &&& .ant-form-item-control-input-content,
  &&& .ant-input-affix-wrapper,
  &&& input.ant-input,
  &&& textarea.ant-input,
  &&& .ant-select,
  &&& .ant-select-selector,
  &&& .ant-picker,
  &&& .phoneInput-country,
  &&& .PhoneInput,
  &&& .PhoneInputInput {
    max-width: 100% !important;
    width: 100% !important;
  }

  .subTitle1 {
    /* Web view */
    color: #070707;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: 32px;

    /* Mobile view */
    @media (max-width: 768px) {
      color: #000;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'inter')}, sans-serif;
      font-size: 16px;
      font-weight: 350;
      line-height: 24px;
      letter-spacing: 0.16px;
    }
  }
  .additionText {
    color: #000;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0.16px;
  }

  h4 {
    margin-bottom: 24px;
  }
    .ContinueButton{
    margin-top:20px;
    }

  @media (max-width: ${theme.breakpoints.md}px) {
    section {
      margin-top: 24px;
    }
  }
`;

const QuestionBlock = styled.div`
  margin-top: 24px;
  width: 100%;

  .ant-form-item,
  .ant-form-item-control-input,
  .ant-form-item-control,
  .ant-input-affix-wrapper,
  input.ant-input,
  textarea.ant-input {
    max-width: 100% !important;
    width: 100% !important;
  }

  .charLimitCaption {
    color: #808080;
    font-size: 12px;
    font-weight: 400;
    margin-top: 4px;
    margin-bottom: 0;
  }
`;

const QuestionHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

const QuestionTitle = styled.h4`
  color: #070707;
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
  margin: 0 !important;
  flex: 1;
`;

const QuestionSubTitle = styled.p`
  color: #262626;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  margin-top: 10px;
  margin-bottom: 0;
  width: 100%;
`;

const ToggleRow = styled.div<any>`
  display: flex;
  flex-direction: ${({ dir }) => (dir === 'rtl' ? 'row-reverse' : 'row')};
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  margin-bottom: 20px;
  width: 100%;
`;

const ToggleLabel = styled.span`
  color: #070707;
  font-size: 16px;
  font-weight: 400;
  line-height: 32px;  
font-family: var(--Font-Family, Tajawal);
font-style: normal;
font-weight: 400;
letter-spacing: 0.16px;
`;

// @ts-ignore
export default Managers;
// @ts-ignore

